import React, { useState, useCallback, useRef } from 'react';

import classnames from 'classnames';

import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import TabSwitch from 'components/uiLibrary/TabSwitch';
import Typography from 'components/uiLibrary/Typography';

import { COMPONENTS, SECTIONS } from 'components/Globals/Analytics/constants';
import EntityCard from 'components/Globals/EntityCard';
import InfiniteListPage from 'components/Globals/Layout/InfiniteListPage';
import StickyContentMarker from 'components/Globals/StickyHeaders';
import Testimonials from 'components/Globals/Testimonials';

import { TP } from 'constants/index';

import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';

import queries from 'containers/Organizations/queries';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './TestimonialsAndCustomers.module.scss';

const FOOTER_TAB_VALUES = {
  TESTIMONIALS: 'testimonials',
  CUSTOMERS: 'customers',
};

const TestimonialCustomerTabSwitch = ({ value, onChange }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  return (
    <TabSwitch
      options={[
        { label: t(`${TP}.FN_TESTIMONIALS`), value: FOOTER_TAB_VALUES.TESTIMONIALS },
        { label: t(`${TP}.FN_CUSTOMERS`), value: FOOTER_TAB_VALUES.CUSTOMERS },
      ]}
      value={value}
      onChange={onChange}
    />
  );
};

const CustomerList = () => (
  <InfiniteListPage
    query={queries.getOrganizations({
      limit: 10,
      filters: {
        is_pro: true,
        avoid_in_testimonials: false,
      },
    })}
  >
    {({ pages }) => {
      const customers = pages?.flatMap(page => page.data);

      return (
        <ul className={classes.customerList}>
          {customers?.map(customer => (
            <li key={customer?.id} className={classes.customerList__item}>
              <EntityCard
                entityType={ENTITY_TYPES.ORGANIZATION}
                entity={customer}
                typographyProps={{
                  subtext: {
                    truncate: true,
                  },
                }}
                isLinkShallow={false}
                trackingData={{
                  section: SECTIONS.CUSTOMER_LIST,
                  component: COMPONENTS.ORGANIZATION_CARD,
                  entityId: customer?.id,
                  entityName: customer?.name,
                  entityType: ENTITY_TYPES.ORGANIZATION,
                }}
              />
              <SpriteIcon className={classes.rightIcon} icon="chevron_right" size={18} />
            </li>
          ))}
        </ul>
      );
    }}
  </InfiniteListPage>
);

const TestimonialsAndCustomers = ({ isLandingPage = false, forceMobileLayout = false }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const customersRef = useRef(null);
  const testimonialsRef = useRef(null);
  const { isDesktop } = useDeviceTypeLayouts();
  const shouldRenderDesktopLayout = !forceMobileLayout && isDesktop;
  const [activeFooterTab, setActiveTab] = useState(FOOTER_TAB_VALUES.TESTIMONIALS);

  const onTabChange = useCallback((e, option) => {
    if (option?.value === FOOTER_TAB_VALUES.TESTIMONIALS) {
      setActiveTab(FOOTER_TAB_VALUES.TESTIMONIALS);
      testimonialsRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      setActiveTab(FOOTER_TAB_VALUES.CUSTOMERS);
      customersRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    // NOTE: The function is accessing testimonialsRef.current and customersRef.current latest values always
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.root}>
      <StickyContentMarker
        name="footer"
        stackIdentifier="content"
        topPositionPx={60}
        renderHeader={() => {
          if (shouldRenderDesktopLayout) {
            return (
              <Typography variant="p" size={18} weight="bold">
                {t(`${TP}.FN_TESTIMONIALS_AND_CUSTOMERS`)}
              </Typography>
            );
          }

          return (
            <div className={classes.tabSwitchStickyContainer}>
              <TestimonialCustomerTabSwitch value={activeFooterTab} onChange={onTabChange} />
            </div>
          );
        }}
      />
      <div className={classnames(classes.container, { [classes.landingPage]: isLandingPage })}>
        {shouldRenderDesktopLayout ? (
          <div className={classes.root__wrapper}>
            <div className={classes.root__wrapper_left}>
              <Typography variant="h2" size={isLandingPage ? 16 : 14} weight="bold">
                {t(`${TP}.FN_TESTIMONIALS`)}
              </Typography>
              <Testimonials className={classes.testimonials} limit={50} inLine hideTitle />
            </div>
            <div className={classes.root__wrapper_right}>
              <Typography variant="h2" size={isLandingPage ? 16 : 14} weight="bold">
                {t(`${TP}.FN_CUSTOMERS`)}
              </Typography>
              <CustomerList />
            </div>
          </div>
        ) : (
          <>
            <div className={classes.root__tabSwitch}>
              <TestimonialCustomerTabSwitch value={activeFooterTab} onChange={onTabChange} />
            </div>
            <div className={classes.root__tabContent}>
              <div className={classes.root__tabContent_container}>
                <div className={classes.testimonialsPositionMarker} ref={testimonialsRef} />
                <Testimonials className={classes.testimonials} limit={50} inLine hideTitle />
              </div>
              <div className={classes.root__tabContent_container}>
                <div className={classes.customersPositionMarker} ref={customersRef} />
                <StickyContentMarker
                  name="customers"
                  stackIdentifier="content"
                  topPositionPx={60}
                  renderHeader={() => {
                    if (shouldRenderDesktopLayout) {
                      return (
                        <Typography variant="p" size={18} weight="bold">
                          {t(`${TP}.FN_TESTIMONIALS_AND_CUSTOMERS`)}
                        </Typography>
                      );
                    }

                    return (
                      <div className={classes.tabSwitchStickyContainer}>
                        <TestimonialCustomerTabSwitch value={activeFooterTab} onChange={onTabChange} />
                      </div>
                    );
                  }}
                  onScrolledBeyond={({ isBeyond }) => {
                    setActiveTab(isBeyond ? FOOTER_TAB_VALUES.CUSTOMERS : FOOTER_TAB_VALUES.TESTIMONIALS);
                  }}
                />
                <Typography variant="p" size={14} weight="bold">
                  {t(`${TP}.FN_CUSTOMERS`)}
                </Typography>
                <CustomerList />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TestimonialsAndCustomers;
